import type {
  ConversationThreadDetail,
  ConversationThreadListItem,
  MessageCategory,
  MessageDocumentItem,
  MessageRegardingType,
} from "@shared/types/Message";
import apiClient from "@/shared/api/apiClient";
import { API_ROUTES } from "@/constants/apiRoutes";
import { axiosInstance } from "@/shared/api/axios";

export async function listMyMessageThreads(): Promise<ConversationThreadListItem[]> {
  const data = await apiClient.get<{ threads: ConversationThreadListItem[] }>(
    API_ROUTES.messages.threads,
  );
  return data.threads;
}

export async function getMyMessageThread(threadId: string): Promise<ConversationThreadDetail> {
  const data = await apiClient.get<{ detail: ConversationThreadDetail }>(
    API_ROUTES.messages.thread(threadId),
  );
  return data.detail;
}

export async function createMyMessageThread(input: {
  providerId: string;
  subject?: string | null;
  category?: MessageCategory;
  regardingType?: MessageRegardingType;
  regardingLeadId?: string | null;
  regardingAppointmentId?: string | null;
  messageBody: string;
  messageCategory?: MessageCategory;
}) {
  return apiClient.post<{ threadId: string; messageId: string; message: string }>(
    API_ROUTES.messages.threads,
    input,
  );
}

export async function sendMyMessage(threadId: string, input: {
  messageBody: string;
  category?: MessageCategory;
}) {
  return apiClient.post<{ message: { id: string } }>(
    API_ROUTES.messages.threadMessages(threadId),
    input,
  );
}

export async function getMyMessageUnreadCount(): Promise<number> {
  const data = await apiClient.get<{ unreadCount: number }>(API_ROUTES.messages.unreadCount);
  return data.unreadCount;
}

export async function listMyMessageDocuments(): Promise<MessageDocumentItem[]> {
  const data = await apiClient.get<{ documents: MessageDocumentItem[] }>(
    API_ROUTES.messages.documents,
  );
  return data.documents;
}

export async function listMyThreadMessageDocuments(threadId: string): Promise<MessageDocumentItem[]> {
  const data = await apiClient.get<{ documents: MessageDocumentItem[] }>(
    API_ROUTES.messages.threadDocuments(threadId),
  );
  return data.documents;
}

export async function uploadMyMessageDocument(input: {
  threadId: string;
  messageId: string;
  file: File;
}): Promise<{ document: MessageDocumentItem }> {
  const formData = new FormData();
  formData.append("file", input.file);
  const response = await axiosInstance.post<{ document: MessageDocumentItem }>(
    API_ROUTES.messages.messageDocuments(input.threadId, input.messageId),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
}

export async function getMyMessageDocumentAccess(input: {
  threadId: string;
  documentId: string;
  action?: "viewed" | "downloaded";
}) {
  const response = await axiosInstance.get<{ accessUrl: string }>(
    API_ROUTES.messages.documentAccess(input.threadId, input.documentId),
    { params: { action: input.action ?? "viewed" } },
  );
  return response.data;
}

function getFileNameFromContentDisposition(value: string | undefined) {
  if (!value) return null;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

export async function fetchMyMessageDocumentBlob(input: {
  threadId: string;
  documentId: string;
  action?: "viewed" | "downloaded";
}): Promise<{ blob: Blob; fileName: string }> {
  const response = await axiosInstance.get<Blob>(
    API_ROUTES.messages.documentAccess(input.threadId, input.documentId),
    {
      params: { action: input.action ?? "viewed" },
      responseType: "blob",
    },
  );
  const contentDisposition = String(response.headers["content-disposition"] ?? "");

  return {
    blob: response.data,
    fileName:
      getFileNameFromContentDisposition(contentDisposition) ??
      `message-document-${input.action === "downloaded" ? "download" : "view"}`,
  };
}

export async function deleteMyMessageDocument(threadId: string, documentId: string) {
  return apiClient.delete<{ message: string }>(
    API_ROUTES.messages.documentDelete(threadId, documentId),
  );
}
